import React from "react";
import { useTranslation } from "react-i18next";
import { CategoryBadge } from "../../../../../utils/categoryHelper";
interface BadgesRowProps {
    matrix: string | null;
    category: { name: string; color?: string; isDefault?: boolean } | null;
    menuItemName: string | null | undefined;
    costPriceType: string | null | undefined;
    showTypeSelector: boolean;
    onCategoryClick: () => void;
    onRevenueLinkClick: () => void;
    onToggleTypeSelector: () => void;
    onTypeChange: (newType: string | null) => void;
    // Add-ons (BYO CHOICE rows) only show their category + a static "Add-On"
    // badge — they're never linked to a revenue item and have no FOOD/DRINKS type.
    isAddOn?: boolean;
}

const getMatrixBgColor = (matrix: string | null): string => {
    if (matrix === "Star") return "#16B364";
    if (matrix === "Dog") return "#F04438";
    if (matrix === "Puzzle") return "#2E90FA";
    if (matrix === "Plowhorse") return "#F1A62D";
    return "#E5E7EB";
};

const getMatrixIcon = (matrix: string | null): { src: string; alt: string } | null => {
    if (matrix === "Star") return { src: "/assets/star.svg", alt: "Star" };
    if (matrix === "Dog") return { src: "/assets/dog.svg", alt: "Dog" };
    if (matrix === "Puzzle") return { src: "/assets/puzzle.svg", alt: "Puzzle" };
    if (matrix === "Plowhorse") return { src: "/assets/plowhorse.svg", alt: "Plowhorse" };
    return null;
};

const BadgesRow: React.FC<BadgesRowProps> = ({
    matrix, category, menuItemName, costPriceType,
    showTypeSelector, onCategoryClick, onRevenueLinkClick, onToggleTypeSelector, onTypeChange,
    isAddOn = false
}) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-wrap items-center gap-2">
            {matrix && (() => {
                const icon = getMatrixIcon(matrix);
                return (
                    <div
                        className="inline-flex items-center gap-[6px] rounded-full px-3 h-[30px] text-[14px] font-medium text-white dark:text-white"
                        style={{ backgroundColor: getMatrixBgColor(matrix) }}
                    >
                        {icon && <img src={icon.src} alt={icon.alt} />}
                        <span>{matrix}</span>
                    </div>
                );
            })()}

            <button
                type="button"
                className="hover:opacity-80 transition-all"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCategoryClick(); }}
            >
                <CategoryBadge
                    categoryName={category?.name || "Uncategorized"}
                    categoryColor={category?.color}
                    isDefaultCategory={category?.isDefault || false}
                />
            </button>

            {/* Add-ons get a single static "Add-On" badge instead of the
                revenue link + FOOD/DRINKS type controls. */}
            {isAddOn && (
                <span className="inline-block px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-medium rounded-md">
                    {t('costPrice.byo.addOn')}
                </span>
            )}

            {!isAddOn && (menuItemName ? (
                <button
                    type="button"
                    className="inline-block px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm font-medium rounded-md hover:bg-green-100 dark:hover:bg-green-800/40 transition-colors"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRevenueLinkClick(); }}
                    title="Linked to revenue item"
                >
                    {menuItemName}
                </button>
            ) : (
                <button
                    type="button"
                    className="inline-block px-2 py-1 bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400 text-sm font-medium rounded-md hover:bg-gray-100 dark:hover:bg-gray-800/40 transition-colors"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRevenueLinkClick(); }}
                    title="Not linked to any revenue item"
                >
                    No link
                </button>
            ))}

            {/* Type badge for unlinked items */}
            {!isAddOn && !menuItemName && (() => {
                const currentType = costPriceType;
                const typeLabel = currentType === 'FOOD' ? t('costPrice.food') : currentType === 'DRINKS' ? t('costPrice.drinks') : t('costPrice.noType');
                const typeBgClass = currentType === 'FOOD'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/40'
                    : currentType === 'DRINKS'
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/40'
                    : 'bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/40';
                return (
                    <div className="relative">
                        <button
                            type="button"
                            className={`inline-block px-2 py-1 text-sm font-medium rounded-md transition-colors ${typeBgClass}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleTypeSelector(); }}
                            title={t('costPrice.setType')}
                        >
                            {typeLabel}
                        </button>
                        {showTypeSelector && (
                            <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[120px]">
                                {[
                                    { value: 'FOOD', label: t('costPrice.food'), color: 'text-green-600 dark:text-green-400' },
                                    { value: 'DRINKS', label: t('costPrice.drinks'), color: 'text-blue-600 dark:text-blue-400' },
                                    { value: null, label: t('costPrice.noType'), color: 'text-gray-500 dark:text-gray-400' }
                                ].map((option) => (
                                    <button
                                        key={option.value || 'none'}
                                        type="button"
                                        className={`w-full text-left px-3 py-1.5 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 ${option.color} ${currentType === option.value || (!currentType && !option.value) ? 'bg-gray-50 dark:bg-gray-750' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onTypeChange(option.value);
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })()}
        </div>
    );
};

export default BadgesRow;
